/**
 * Input sanitisation for BYOR (§4.8.2 step 1, FR-56, NFR-7b).
 *
 * All BYOR input — fetched pages, pasted HTML, pasted text — is untrusted. This
 * module is the only place it is allowed to become structure, and it does so
 * without a DOM parser and without ever producing markup that could be
 * rendered.
 *
 * Two properties matter and are tested directly:
 *  - Script, style, iframe, object, embed, template, and comment content is
 *    *removed*, not escaped — its text must never reach the extractor, or a
 *    `<script>` block full of player names could win the confidence scoring.
 *  - The output of every function here is plain text or plain-text cells. The
 *    preview UI renders extracted values as escaped text in the tool's own
 *    table; source markup never reaches the DOM (FR-56).
 *
 * Pure module — no I/O, no DOM.
 */

/** Elements whose *contents* are discarded entirely, not just untagged. */
const DROP_CONTENT = ['script', 'style', 'iframe', 'object', 'embed', 'template', 'noscript', 'svg', 'canvas'];

export function stripDangerousBlocks(html: string): string {
  let out = html;

  // HTML comments first — they can hide anything, including other elements.
  out = out.replace(/<!--[\s\S]*?-->/g, ' ');

  for (const tag of DROP_CONTENT) {
    // Non-greedy, case-insensitive, tolerant of attributes and of a missing
    // close tag at end of input.
    const paired = new RegExp(`<${tag}\\b[^>]*>[\\s\\S]*?<\\/${tag}\\s*>`, 'gi');
    out = out.replace(paired, ' ');
    const unclosed = new RegExp(`<${tag}\\b[^>]*>[\\s\\S]*$`, 'gi');
    out = out.replace(unclosed, ' ');
    const selfClosing = new RegExp(`<${tag}\\b[^>]*\\/?>`, 'gi');
    out = out.replace(selfClosing, ' ');
  }

  // Inline event handlers and javascript: URLs. Nothing here is ever rendered,
  // so this is belt-and-braces against a future change that does render it.
  out = out.replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, ' ');
  out = out.replace(/javascript:/gi, 'blocked:');

  return out;
}

const ENTITIES: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
  ndash: '-', mdash: '-', rsquo: "'", lsquo: "'", rdquo: '"', ldquo: '"',
  hellip: '…', middot: '·', bull: '•', times: '×', deg: '°',
};

/**
 * Decode entities in a single pass.
 *
 * One pass rather than three chained ones, and that is not a micro-optimisation:
 * chaining lets a later pass re-scan an earlier pass's output, so `&#60;`
 * decoded to `&lt;` by the numeric pass would be decoded again to a literal `<`
 * by the named pass — quietly reconstructing the exact markup this function
 * exists to neutralise.
 */
export function decodeEntities(text: string): string {
  return text.replace(
    /&(?:#x([0-9a-f]+)|#(\d+)|([a-z]+));/gi,
    (whole, hex: string | undefined, dec: string | undefined, name: string | undefined) => {
      if (hex !== undefined) return safeCodePoint(parseInt(hex, 16));
      if (dec !== undefined) return safeCodePoint(parseInt(dec, 10));
      if (name !== undefined) return ENTITIES[name.toLowerCase()] ?? whole;
      return whole;
    },
  );
}

function safeCodePoint(code: number): string {
  if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) return '';
  // Never decode into characters that could reconstruct markup downstream.
  if (code === 0x3c || code === 0x3e) return code === 0x3c ? '&lt;' : '&gt;';
  try {
    return String.fromCodePoint(code);
  } catch {
    return '';
  }
}

/** Collapse whitespace without destroying line structure (plain-text mode
 *  depends on newlines to find numbered-line runs). */
export function normalizeWhitespace(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')
    .replace(/ /g, ' ')
    .replace(/[ \t]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Strip all tags and return readable text. Used for plain-text fallback. */
export function htmlToText(html: string): string {
  const safe = stripDangerousBlocks(html);
  const withBreaks = safe
    .replace(/<\/(tr|p|div|li|h[1-6]|table|section|article)\s*>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(td|th)\s*>/gi, '\t');
  return normalizeWhitespace(decodeEntities(withBreaks.replace(/<[^>]*>/g, ' ')));
}

export function looksLikeHtml(input: string): boolean {
  const sample = input.slice(0, 4000);
  return /<\/?(table|tr|td|th|ol|ul|li|div|span|p|html|body)\b/i.test(sample);
}

/**
 * Escape a value for display. The preview renders through this even though it
 * writes via textContent — defence in depth against a later refactor reaching
 * for innerHTML.
 */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
